import React, { useMemo, useState, useEffect } from "react";
import { useSelector } from "react-redux";

import { Box, Grid, makeStyles, Typography } from "@material-ui/core";

import { COLORS } from "../../../../theme/colors";
import { ANY_FILTER_KEY, ITEMS_PER_PAGE } from "../../constants";
import { UNITS_FILTERS } from "../../../../services/utils";
import SearchIcon from "../../../../assets/images/icons/search.svg";
import { Table } from "../../../../components/table";
import { TableFilter } from "../../../../components/parts";
import { Pagination } from "../../../../components/pagination";
import { Icon, UnderscoreTextInput } from "../../../../components/base";
import { ActionButtonsGroup } from "../../../../components/buttonsGroup/ActionButtonsGroup";
import { getQuarter, getYear, getData } from "../../../../services";
import { EmptyPage } from "../../../../components/parts";
import { paginationOfExpandableList } from "./unitListRow";

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(4),
  },
  searchContainer: {
    marginBottom: theme.spacing(4),
  },
  searchBtn: {
    marginLeft: theme.spacing(1),
  },
  pagination: {
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(2),
  },
  clearFiter: {
    cursor: "pointer",
    color: theme.palette.primary.main,
    marginLeft: 10,
    fontWeight: 400,
  },
  backToList: {
    cursor: "pointer",
    color: COLORS.blue,
    paddingRight: 10,
    borderRight: `1px solid ${COLORS.darks[0][25]}`,
  },
  backBlock: {
    display: "flex",
    alignItems: "center",
  },
  buildingGroup: {
    cursor: "pointer",
    height: "20px",
    lineHeight: "20px",
    letter: "0.2 px",
    fontWeight: "bold",
  },
  pointer: {
    cursor: "pointer",
    height: "20px",
    letter: "0.2 px",
  },
  unitsCnt: {
    height: "24px",
    fontWeight: "bold",
  },
}));

const defaultFilters = {
  tenancy_status: ANY_FILTER_KEY,
  leasing_end: ANY_FILTER_KEY,
  electricity_status: ANY_FILTER_KEY,
  area: ANY_FILTER_KEY,
};

export const UnitsList = ({
  buildingId,
  complexId,
  setShowUnitList,
  onSelected,
  formatter,
  propertyFlag = true,
}) => {
  const classes = useStyles();
  const { user } = useSelector((state) => state.auth);
  const [units, setUnits] = useState(null);
  const [unitsCnt, setUnitsCnt] = useState(0);
  const [tableColumnWidths, setTableColumnWidths] = useState([]);
  const [filters, setFilters] = useState(defaultFilters);
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(0);
  const [paginationCalculated, setPaginationCalculated] = useState({
    cnt: 0,
    pageSizes: [],
    pageStarts: [],
    pageEnds: [],
  });
  const [unitFilters, setUnitFilters] = useState(UNITS_FILTERS(user?.unit_system));
  const isGrouped = buildingId ? false : true;
  const currencySymbol = useSelector((state) => state.auth.user.currency.symbol);
  const { cluster, segments, selectedCountries } = useSelector((state) => state.selectCSC);

  useEffect(() => {
    const url = buildingId
      ? `/praas/v2/buildings/${buildingId}/properties/units/`
      : complexId
        ? `/praas/v2/complexes/${complexId}/properties/units/`
        : "/praas/v2/buildings/properties/units/";

    getData(url, buildingId ? setUnits : setUnitsGrouped);

    return () => setUnits([]);
  }, [buildingId, complexId, cluster, segments, selectedCountries]);

  const filtersCount = useMemo(() => {
    const thisQ = getQuarter();
    const thisYear = getYear();
    const count = {
      this_q: 0,
      this_year: 0,
      area_large: 0,
      area_mid: 0,
      area_small: 0,
    };
    if (units === null) {
      return 0;
    }
    for (const unit of units) {
      count[unit.tenancy_status] = (count[unit.tenancy_status] || 0) + 1;
      count[unit.electricity_status] = (count[unit.electricity_status] || 0) + 1;

      if (thisYear === getYear(new Date(unit.leasing_end))) {
        count.this_year += 1;
        if (thisQ === getQuarter(new Date(unit.leasing_end))) {
          count.this_q += 1;
        }
      }

      if (unit.area < (user?.has_sqm_unit ? 30 : 2500)) {
        count.area_small += 1;
      } else if (
        unit.area >= (user?.has_sqm_unit ? 30 : 2500) &&
        unit.area < (user?.has_sqm_unit ? 40 : 5000)
      ) {
        count.area_mid += 1;
      } else if (unit.area >= (user?.has_sqm_unit ? 40 : 5000)) {
        count.area_large += 1;
      }
    }
    return count;
  }, [units]);

  const filterUnits = useMemo(() => {
    let show_flag = false;
    return units?.filter((unit) => {
      // show/hide decision based on search text
      if (searchText !== "" && searchText.length > 2) {
        if (isGrouped && unit?.unit_info) {
          const tmp = unit.unit_info;
          if (
            tmp.filter((val) => val.unit_id.toLowerCase().includes(searchText.toLowerCase()) > 0)
              .length > 0
          ) {
            show_flag = true;
            unit.expanded = true;
          } else if (
            unit.name.toLowerCase().includes(searchText.toLowerCase()) ||
            unit.address.toLowerCase().includes(searchText.toLowerCase())
          ) {
            show_flag = false;
            return true;
          } else {
            show_flag = false;
            unit.expanded = false;
          }
        } else if (!isGrouped) {
          if (unit.unit_id.toLowerCase().includes(searchText.toLowerCase())) {
            show_flag = true;
            unit.expanded = true;
          } else {
            show_flag = false;
            unit.expanded = false;
          }
        }
        // if search by text filtering by values (code below) ignored
        if (show_flag === false) {
          return false;
        }
      }
      // otherwise decide based on user selections
      else if (typeof unit.expanded !== "undefined") {
        show_flag = unit.expanded;
      }

      if (isGrouped && show_flag === false && unit.isGroup === undefined) {
        return false;
      }

      if (
        filters.tenancy_status !== ANY_FILTER_KEY &&
        unit.tenancy_status !== filters.tenancy_status
      ) {
        return false;
      }

      if (
        filters.electricity_status !== ANY_FILTER_KEY &&
        unit.electricity_status !== filters.electricity_status
      ) {
        return false;
      }
      if (
        filters.leasing_end === "this_year" &&
        unit.leasing_end &&
        getYear() !== getYear(new Date(unit.leasing_end))
      ) {
        return false;
      }

      if (
        filters.leasing_end === "this_q" &&
        unit.leasing_end &&
        (getYear() !== getYear(new Date(unit.leasing_end)) ||
          getQuarter() !== getQuarter(new Date(unit.leasing_end)))
      ) {
        return false;
      }

      if (filters.area === "area_large" && unit.area < (user?.has_sqm_unit ? 40 : 5000)) {
        return false;
      }
      if (
        filters.area === "area_mid" &&
        ((user?.has_sqm_unit ? 30 : 2500) > unit.area ||
          unit.area >= (user?.has_sqm_unit ? 40 : 5000))
      ) {
        return false;
      }
      if (filters.area === "area_small" && unit.area >= (user?.has_sqm_unit ? 30 : 2500)) {
        return false;
      }
      return true;
    });
  }, [units, filters, searchText]);

  const selectUnit = (e, i) => {
    e.stopPropagation();
    expandCollapse(i);
  };

  useEffect(() => {
    if (filterUnits === undefined) return;
    const columns = filterUnits.map((e) =>
      e.isGroup ? formatter.groupColWidths : formatter.colWidths
    );
    setTableColumnWidths(columns);
    setPaginationCalculated(
      paginationOfExpandableList(filterUnits, 3, "isGroup", "unit_info", "expanded")
    );
  }, [filterUnits]);

  const tableData = useMemo(() => {
    const items = filterUnits?.slice(
      paginationCalculated.pageStarts[page],
      paginationCalculated.pageEnds[page]
    );

    const res = items?.map((item, i) => {
      if (item?.isGroup) {
        return formatter.generateGroupRow(item, i, selectUnit);
      } else {
        return formatter.generateRow(item, i, currencySymbol);
      }
    });

    return res;
  }, [page, filterUnits, units, tableColumnWidths, paginationCalculated]);

  const onOptionSelect = (filterKey, optionKey) => {
    const newFilters = { ...filters, [filterKey]: optionKey };
    setFilters(newFilters);
    setPage(0);
  };

  const setUnitsGrouped = (value) => {
    const res = value.map((e) => ({ ...e, isGroup: true, expanded: false }));
    const unitsCnt = res.reduce((acc, item) => {
      acc = acc + item.unit_info.length;
      return acc;
    }, 0);
    setUnitsCnt(unitsCnt);
    const len = res.length;
    for (var i = len - 1; i >= 0; i--) {
      res.splice(i + 1, 0, ...res[i].unit_info);
    }
    res[0].expanded = true;
    setUnits(res);
  };

  const expandCollapse = (i) => {
    const mappedIndex = units.findIndex(
      (e) => e.isGroup && e.id === filterUnits[i + paginationCalculated.pageStarts[page]].id
    );
    if (units[mappedIndex]?.expanded) {
      let tmp = [...units];
      tmp[mappedIndex].expanded = false;

      setUnits(tmp);
    } else {
      let tmpp = [...units];
      tmpp[mappedIndex].expanded = true;

      setUnits(tmpp);
    }
  };

  const setSearchTextCollapsable = (e) => {
    if (e === "") {
      const res = units.map((item) => {
        if (item?.expanded) {
          return { ...item, expanded: false };
        } else {
          return item;
        }
      });
      setUnits(res);
    }
    setSearchText(e);
  };

  return (
    <Grid className={classes.root} container>
      {propertyFlag ? (
        <Grid style={{ marginBottom: 40 }} container justifyContent="space-between">
          <Typography style={{ fontWeight: "bold" }}>
            UNITS LIST ({!isGrouped ? (units ? units.length : 0) : unitsCnt})
            <Typography
              component="span"
              className={classes.clearFiter}
              onClick={() => setFilters(defaultFilters)}
            >
              Clear filters
            </Typography>
          </Typography>
          <Box className={classes.backBlock}>
            <Typography onClick={() => setShowUnitList(false)} className={classes.backToList}>
              Back to Property data
            </Typography>
            <ActionButtonsGroup hideIntegrateBtn />
          </Box>
        </Grid>
      ) : (
        <div />
      )}
      {units !== null ? (
        units.length ? (
          <>
            <Grid item xs={2}>
              <TableFilter
                count={units.length}
                filters={filters}
                dataFilters={unitFilters}
                filtersCount={filtersCount}
                onOptionSelect={onOptionSelect}
                setDataFilters={setUnitFilters}
              />
            </Grid>
            <Grid item xs={10}>
              <Grid className={classes.searchContainer} container>
                <UnderscoreTextInput
                  value={searchText}
                  onChange={setSearchTextCollapsable}
                  placeHolder="Search for unit..."
                  width="50%"
                />
                <Icon root={classes.searchBtn} src={SearchIcon} hoverable />
              </Grid>
              <Table
                headers={formatter.headers}
                data={tableData}
                colWidths={formatter.colWidths}
                colWidthsVariable={
                  isGrouped
                    ? tableColumnWidths.slice(
                      paginationCalculated.pageStarts[page],
                      paginationCalculated.pageEnds[page]
                    )
                    : undefined
                }
                cellPaddingRight={0}
                cellStyles={[{ padding: "18px 0 18px 24px" }]}
                maxHeight={1000}
                onRowClick={(rowIndex) =>
                  isGrouped
                    ? onSelected(filterUnits[paginationCalculated.pageStarts[page] + rowIndex])
                    : onSelected(filterUnits[page * ITEMS_PER_PAGE + rowIndex])
                }
              />
              <Pagination
                root={classes.pagination}
                totalPage={
                  isGrouped
                    ? Math.ceil(paginationCalculated.pageSizes.length)
                    : Math.ceil(filterUnits.length / ITEMS_PER_PAGE)
                }
                onChange={setPage}
                page={page}
              />
            </Grid>{" "}
          </>
        ) : (
          <EmptyPage headerText="units" footerText="Unit" />
        )
      ) : (
        <div />
      )}
    </Grid>
  );
};
